/*
 * bnh-addons.js — native paid add-ons for the product page.
 * ---------------------------------------------------------------------------
 * Replaces OPTIS (BSS) product options for PAID accessories.
 *
 * Selected accessories are added to the cart as REAL product lines alongside
 * the main item, using Shopify's multi-item form encoding:
 *     items[0][id], items[0][quantity], items[1][id], ...
 * so the price lands in the subtotal and accessory stock actually decrements.
 *
 * Child lines carry `properties[_bnh_parent]` = the main variant id, which the
 * cart templates use to nest them visually under their parent.
 *
 * Integration: the theme's <product-form> binds a bubble-phase `submit`
 * listener. We bind in the CAPTURE phase so we run first, and we only take
 * over when at least one accessory is selected — otherwise the theme's own
 * flow is left completely untouched.
 */
class BnhAddons extends HTMLElement {
  connectedCallback() {
    this.selections = new Map(); // handle -> {id,title,price,priceText,label}
    this.summary = this.querySelector('.bnh-addons__summary');
    this.chosenList = this.querySelector('.bnh-addons__chosen');
    this.totalEl = this.querySelector('[data-addons-total]');

    this.querySelectorAll('.bnh-addon').forEach((group) => {
      const panel = this.panelFor(group.dataset.addon);
      if (panel) this.bindGroup(group, panel);
    });

    this.form =
      document.getElementById(this.dataset.formId) ||
      document.querySelector('form[action*="/cart/add"]');

    if (this.form && !this.form.__bnhBound) {
      this.form.__bnhBound = true;
      this.form.addEventListener('submit', this.onSubmit.bind(this), true);
    }
  }

  panelFor(handle) {
    return this.querySelector('.bnh-addon__panel[data-addon="' + CSS.escape(handle) + '"]');
  }

  tileFor(handle) {
    return this.querySelector('.bnh-addon[data-addon="' + CSS.escape(handle) + '"]');
  }

  bindGroup(group, panel) {
    const tile = group.querySelector('.bnh-addon__tile');
    const clear = panel.querySelector('.bnh-addon__clear');

    tile.addEventListener('click', () => {
      const open = !panel.hidden;
      // close siblings so only one chooser is open at a time
      this.querySelectorAll('.bnh-addon__panel').forEach((p) => { p.hidden = true; });
      this.querySelectorAll('.bnh-addon__tile').forEach((t) => t.setAttribute('aria-expanded', 'false'));
      panel.hidden = open;
      tile.setAttribute('aria-expanded', String(!open));
      if (!panel.hidden) panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });

    panel.querySelectorAll('.bnh-variant').forEach((btn) => {
      btn.addEventListener('click', () => {
        const handle = group.dataset.addon;
        const already = this.selections.get(handle);
        const id = btn.dataset.variantId;

        if (already && String(already.id) === String(id)) {
          this.clearGroup(group);            // tapping the chosen one removes it
          return;
        }

        panel.querySelectorAll('.bnh-variant').forEach((b) => {
          b.classList.remove('is-selected');
          b.setAttribute('aria-checked', 'false');
        });
        btn.classList.add('is-selected');
        btn.setAttribute('aria-checked', 'true');

        this.selections.set(handle, {
          id: id,
          title: btn.dataset.variantTitle,
          price: parseInt(btn.dataset.variantPrice, 10) || 0,
          priceText: btn.dataset.variantPriceFormatted,
          label: btn.dataset.parentLabel
        });

        group.classList.add('is-selected');
        const pe = group.querySelector('[data-base-price]');
        if (pe && !pe.dataset.original) pe.dataset.original = pe.textContent.trim();
        if (clear) clear.hidden = false;
        const priceEl = group.querySelector('[data-base-price]');
        if (priceEl) priceEl.textContent = btn.dataset.variantPriceFormatted;
        this.render();
      });
    });

    if (clear) clear.addEventListener('click', () => this.clearGroup(group));
  }

  clearGroup(group) {
    const panel = this.panelFor(group.dataset.addon);
    this.selections.delete(group.dataset.addon);
    group.classList.remove('is-selected');
    if (panel) {
      panel.querySelectorAll('.bnh-variant').forEach((b) => {
        b.classList.remove('is-selected');
        b.setAttribute('aria-checked', 'false');
      });
      const clear = panel.querySelector('.bnh-addon__clear');
      if (clear) clear.hidden = true;
    }
    const priceEl = group.querySelector('[data-base-price]');
    if (priceEl && priceEl.dataset.original) priceEl.textContent = priceEl.dataset.original;
    this.render();
  }

  render() {
    if (!this.summary) return;
    const items = [...this.selections.values()];
    this.summary.hidden = items.length === 0;
    if (!items.length) return;

    this.chosenList.innerHTML = items
      .map((s) => `<li><span>${this.esc(s.label)} — ${this.esc(s.title)}</span><span>${this.esc(s.priceText)}</span></li>`)
      .join('');

    const total = items.reduce((sum, s) => sum + s.price, 0);
    if (this.totalEl) this.totalEl.textContent = this.money(total);
  }

  esc(v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );
  }

  /*
   * Format to match the storefront exactly. The shop renders "4,467.00TL"
   * (comma thousands, dot decimal) - a tr-TR locale would wrongly give
   * "4.467,00TL". So we mirror the pattern of a real rendered price rather
   * than guessing a locale.
   */
  money(cents) {
    try {
      if (window.Shopify && Shopify.formatMoney && window.theme && theme.moneyFormat) {
        return Shopify.formatMoney(cents, theme.moneyFormat);
      }
    } catch (e) {}

    const sample = this.querySelector('.bnh-variant[data-variant-price-formatted]');
    const text = sample ? sample.dataset.variantPriceFormatted : '';
    const suffix = text ? text.replace(/[\d.,\s]/g, '') : 'TL';
    const amount = (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return /^[^\d]/.test(text) ? suffix + amount : amount + suffix;
  }

  onSubmit(evt) {
    if (!this.selections.size) return;          // nothing chosen -> theme handles it
    if (document.body.classList.contains('template-cart')) return;

    evt.preventDefault();
    evt.stopImmediatePropagation();

    const form = evt.target;
    const btn = form.querySelector('[name="add"]');
    if (btn && btn.classList.contains('loading')) return;
    if (btn) { btn.classList.add('loading'); btn.setAttribute('aria-disabled', 'true'); }

    const fd = new FormData(form);
    const mainId = fd.get('id');
    const qty = parseInt(fd.get('quantity'), 10) || 1;

    const items = [{ id: Number(mainId), quantity: qty, properties: {} }];
    fd.forEach((value, key) => {
      const m = key.match(/^properties\[(.+)\]$/);
      if (m && String(value).length) items[0].properties[m[1]] = value;
    });

    this.selections.forEach((s) => {
      items.push({
        id: Number(s.id),
        quantity: qty,
        properties: { _bnh_parent: String(mainId), _bnh_for: s.label }
      });
    });

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data && data.description ? data.description : 'add failed');
        this.selections.forEach((_, handle) => {
          const g = this.tileFor(handle);
          if (g) this.clearGroup(g);
        });
        return this.refreshDrawer();
      })
      .catch((err) => {
        const box = form.closest('product-form');
        const msg = box && box.querySelector('.product-form__error-message');
        const wrap = box && box.querySelector('.product-form__error-message-wrapper');
        if (msg && wrap) { msg.textContent = err.message; wrap.hidden = false; }
        else window.location.href = '/cart';
      })
      .finally(() => {
        if (btn) { btn.classList.remove('loading'); btn.removeAttribute('aria-disabled'); }
      });
  }

  refreshDrawer() {
    const host = document.querySelector('mini-cart');
    const target = document.getElementById('mini-cart');
    const drawer = document.querySelector('cart-drawer');
    if (!host || !target || !host.dataset.url) { window.location.href = '/cart'; return; }

    return fetch(host.dataset.url)
      .then((r) => r.text())
      .then((html) => {
        const sec = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        if (sec) target.innerHTML = sec.innerHTML;
        document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
        if (drawer && drawer.openMenuDrawer) drawer.openMenuDrawer();
      });
  }
}

if (!customElements.get('bnh-addons')) customElements.define('bnh-addons', BnhAddons);
